// Who is signed in, as the screen sees it: when Me's event stream and kept
// cache run, and what happens on a 401.
//
// Promise:
// - Me's event stream (and the cache kept in IndexedDB) runs only while the
//   person is in the app (Me, or a workspace: its chips and favorites follow
//   Me's events): `start` before the first paint, and again (fresh) right
//   after signing in; `end` on signing out or a lost session stops it (and
//   what the host stops with it, `onEnd`: the team stream), removes the kept
//   copies (the query cache's and the datasets') and empties the query cache, so the next person sees nothing of the
//   previous one.
// - A 401 inside the app (the session ended): a waitlisted account goes to
//   /waitlist; in development with auto sign-in the screen signs in once and
//   reloads what failed; otherwise it goes to /signin, which brings the person
//   back here afterwards. Elsewhere each page decides for itself (beforeLoad).
// - Signing out never bounces through that 401 handling. What the host does
//   before it (`beforeSignOut`) never stops it.
import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import {
  type Api,
  clearDatasetCopies,
  clearPersistedCache,
  forgetListPlaces,
  type QueryPersister,
} from "@trameria/ui-core";
import { signOut as authSignOut } from "./auth/client";
import { type Account, accountQuery, authConfigQuery } from "./queries";
import { inMePath, inWorkspacePath } from "./workspace/places";

export const DEV_SIGN_IN_PATH = "/api/auth/dev/sign-in";

/** Paths of Me. */
export const inMe = inMePath;

/**
 * Paths of the app (Me, the workspaces, and the operator's place, in the same
 * frame with the rail), where the stream runs and a 401 is handled here.
 */
export const inApp = (path: string) =>
  inMe(path) ||
  inWorkspacePath(path) ||
  path === "/admin" ||
  path.startsWith("/admin/");

export interface CloudSession {
  /** Starts (or restarts, e.g. right after signing in) the stream and the kept cache. */
  start(): Promise<void>;
  /** Starts them unless they run (entering Me). */
  ensure(): Promise<void>;
  /** Stops them and forgets everything the screen held. */
  end(): Promise<void>;
  onUnauthorized(): void;
  signOut(): Promise<void>;
  /** Signs in without a code when the server allows it (development). */
  devSignIn(): Promise<boolean>;
}

export function createCloudSession(deps: {
  api: Api;
  queryClient: QueryClient;
  persister: QueryPersister;
  /** Me's start: restores the kept cache, opens the stream; returns stop. */
  startMe(): Promise<() => void>;
  router(): AnyRouter;
  /**
   * Runs before signing out (this device's notification destination goes
   * away). Must not throw and must not wait long: signing out goes on anyway.
   */
  beforeSignOut?(): Promise<void>;
  /** What else stops when the session ends (the team stream, the badge). */
  onEnd?(): void;
}): CloudSession {
  const { api, queryClient, persister } = deps;
  let running: Promise<() => void> | null = null;
  let handling = false;
  let signingOut = false;

  const stop = async () => {
    const was = running;
    running = null;
    if (was) (await was)();
  };

  const session: CloudSession = {
    async start() {
      await stop();
      running = deps.startMe();
      await running;
    },
    async ensure() {
      if (running) await running;
      else await session.start();
    },
    async end() {
      deps.onEnd?.();
      await stop();
      await clearPersistedCache(persister);
      await clearDatasetCopies();
      forgetListPlaces();
      queryClient.clear();
    },
    async devSignIn() {
      try {
        const config = await queryClient.ensureQueryData(authConfigQuery(api));
        if (!config.devAutoLogin) return false;
        await api(DEV_SIGN_IN_PATH, { method: "POST" });
        return true;
      } catch {
        return false;
      }
    },
    onUnauthorized() {
      // The handler of the event, not the screen's state: where the 401 came from.
      const path = window.location.pathname;
      if (handling || signingOut || !inApp(path)) return;
      handling = true;
      const back = path + window.location.search;
      void (async () => {
        try {
          const router = deps.router();
          const account = await queryClient
            .fetchQuery({ ...accountQuery(api), staleTime: 0 })
            .catch(() => null as Account | null);
          if (account && !account.approved) {
            await session.end();
            await router.navigate({ to: "/waitlist", replace: true });
            return;
          }
          if (!account && (await session.devSignIn())) {
            await session.start();
            await router.invalidate();
            return;
          }
          await session.end();
          await router.navigate({
            to: "/signin",
            search: { next: back },
            replace: true,
          });
        } finally {
          handling = false;
        }
      })();
    },
    async signOut() {
      signingOut = true;
      try {
        await deps.beforeSignOut?.().catch(() => {});
        await authSignOut();
        await session.end();
        await deps.router().navigate({ to: "/signin", replace: true });
      } finally {
        signingOut = false;
      }
    },
  };
  return session;
}
